import React, { createContext, useContext, useEffect, useRef } from "react";
import { useBulkAnalyseJob } from "./useBulkAnalyseJob";
import BulkAnalyseConfirmModal from "./components/BulkAnalyseConfirmModal";
import BulkAnalyseJobModal from "./components/BulkAnalyseJobModal";

const BulkAnalyseContext = createContext(null);

/**
 * Presentational provider. Redux state and dispatchers arrive as props from
 * BulkAnalyse.container — use BulkAnalyseProvider from ./index in pages.
 */
export function BulkAnalyseView({
  enabled = false,
  onOpenReport,
  onRunFinished,
  finishedVersion = 0,
  children,
  ...reduxProps
}) {
  const job = useBulkAnalyseJob({ enabled: Boolean(enabled), ...reduxProps });

  // A run reached a final state: reload the timesheet table so the new
  // reports appear in the AI column.
  const lastFinishedRef = useRef(finishedVersion);
  useEffect(() => {
    if (finishedVersion !== lastFinishedRef.current) {
      lastFinishedRef.current = finishedVersion;
      onRunFinished?.();
    }
  }, [finishedVersion, onRunFinished]);

  const openReport = (row) => {
    if (!row?.timesheetId) return;
    onOpenReport?.(row.timesheetId, row.aiStatus);
  };

  return (
    <BulkAnalyseContext.Provider
      value={{ ...job, enabled: Boolean(enabled), onOpenReport: openReport }}
    >
      {children}
      {enabled ? <BulkAnalyseConfirmModal /> : null}
      {enabled ? <BulkAnalyseJobModal /> : null}
    </BulkAnalyseContext.Provider>
  );
}

export function useBulkAnalyse() {
  const value = useContext(BulkAnalyseContext);
  if (!value) {
    throw new Error("useBulkAnalyse must be used within BulkAnalyseProvider");
  }
  return value;
}
